import { Popover, Checkbox, Radio, Icon } from 'antd';
import { FilterTypes, IFilterOption, useFilters } from './context';
import { FormattedMessage } from 'react-intl';
import dayjs from 'dayjs';
import { useState, Fragment, useEffect } from 'react';
import 'react-datepicker/dist/react-datepicker.css';
import { useIntl } from 'react-intl';
import { DatePicker } from 'Components/nui';

function findByValue(arr, value) {
  if (!Array.isArray(arr)) return null;
  for (let obj of arr) {
    if (obj.value === value) {
      return obj;
    }
    if (obj.children && Array.isArray(obj.children)) {
      return findByValue(obj.children, value);
    }
  }
  return null;
}

const FilterDropdown = ({
  id,
  label,
  menuLabel,
  type,
  options,
  showSelectedCount = true,
  subFieldId,
  subFieldType,
  onChange,
  subFieldOnChange,
  onClear,
  canSelectAll = false,
}: {
  id: string;
  label: React.ReactNode;
  menuLabel?: React.ReactNode;
  type: FilterTypes;
  options?: IFilterOption[];
  showSelectedCount?: boolean;
  subFieldId?: string;
  subFieldType?: FilterTypes;
  onChange?: (value: any) => void;
  subFieldOnChange?: (value: any) => void;
  onClear?: () => void;
  canSelectAll?: boolean;
}) => {
  const [visible, setVisible] = useState(false);
  const { filters, setFilters } = useFilters();
  const values = filters[id]?.items ?? [];
  const subValues = subFieldId ? (filters[subFieldId]?.items ?? []) : [];

  const clearDropdownFilter = (id: string) => {
    const newFilters = { ...filters };
    if (newFilters[id]) {
      newFilters[id].items = [];
    }
    if (subFieldId && newFilters[subFieldId]) {
      newFilters[subFieldId].items = [];
    }
    setFilters(newFilters);
    onClear && onClear();
  };

  const handleCheckboxChange = (id: string, option: any) => {
    let newFilters = { ...filters };
    if (!newFilters[id]) {
      newFilters[id] = {
        type: FilterTypes.CHECKBOXES,
        items: [],
      };
    }

    const isArrayValue = Array.isArray(option.value);
    const optionValues = isArrayValue ? option.value : [option.value];
    const isChecked = optionValues.every((v) =>
      newFilters[id].items.includes(v)
    );

    if (isChecked) {
      // Unchecking - remove all values
      newFilters[id].items = newFilters[id].items.filter(
        (x) => !optionValues.includes(x)
      );
      // uncheck all children
      option.children?.forEach?.((child) => {
        newFilters[id].items = newFilters[id].items.filter(
          (x) => x != child.value
        );
      });
    } else {
      if (
        option.children?.every?.((child) =>
          newFilters[id].items.includes(child.value)
        )
      ) {
        newFilters[id].items = newFilters[id].items.filter(
          (x) => x != option.value
        );
        option.children.forEach((child) => {
          newFilters[id].items = newFilters[id].items.filter(
            (x) => x != child.value
          );
        });
      } else {
        if (!option.children) {
          // Add all values from the array
          newFilters[id].items.push(...optionValues);
          newFilters[id].items = [...new Set(newFilters[id].items)];
        }
        // check all children
        option.children?.forEach?.((child) => {
          newFilters[id].items.push(child.value);
          newFilters[id].items = [...new Set(newFilters[id].items)];
        });
      }
    }
    setFilters(newFilters);
    onChange && onChange(newFilters);
  };

  const handleDateRangeChange = (id: string, values: any, ret: boolean) => {
    const newFilters = { ...filters };
    if (!newFilters[id]) {
      newFilters[id] = { type: FilterTypes.DATE, items: [] };
    }
    newFilters[id].items = values;
    if (ret) {
      return newFilters;
    }
    setFilters(newFilters);
  };

  const handleRadioChange = (id: string, option: any, ret: boolean) => {
    const newFilters = { ...filters };
    if (!newFilters[id]) {
      newFilters[id] = {
        type: FilterTypes.RADIO,
        items: [],
      };
    }
    if (option.value === '') {
      newFilters[id].items = [];
      return newFilters;
    }
    newFilters[id].items = [option.value];
    if (ret) {
      return newFilters;
    }
    setFilters(newFilters);
    onChange && onChange(newFilters);
  };

  const panel = (
    <>
      {<h4 className="filter-dropdown-title">{menuLabel ?? label}</h4>}
      {type === FilterTypes.RADIO && (
        <Radio.Group value={values[0] ?? ''}>
          <ul className="filter-dropdown-list">
            {options?.map?.((option, k) => (
              <li key={k}>
                <Radio
                  value={option.value}
                  onChange={() => handleRadioChange(id, option)}
                >
                  {option.translation ? (
                    <FormattedMessage
                      id={option.translation.id}
                      description={option.translation.description}
                      defaultMessage={option.label}
                    />
                  ) : (
                    option.label
                  )}
                </Radio>
                {option.hasSubField &&
                  subFieldId &&
                  subFieldType === FilterTypes.DATE && (
                    <SubFieldDate
                      id={id}
                      subFieldId={subFieldId}
                      option={option}
                      handleDateRangeChange={handleDateRangeChange}
                      handleRadioChange={handleRadioChange}
                      subFieldOnChange={subFieldOnChange}
                    />
                  )}
              </li>
            ))}
          </ul>
        </Radio.Group>
      )}

      {type === FilterTypes.CHECKBOXES && (
        <ul className="filter-dropdown-list">
          {options?.map?.((option, k) => {
            const isGroup = option.children && option.children.length > 0;

            const allChilrenChecked =
              isGroup &&
              option.children?.every((child) => values.includes(child.value));

            const someChilrenChecked =
              isGroup &&
              !allChilrenChecked &&
              option.children?.some((child) => values.includes(child.value));

            const isArrayValue = Array.isArray(option.value);
            const isChecked = isArrayValue
              ? option.value.every((v) => values.includes(v))
              : values.includes(option.value);

            return (
              <Fragment key={k}>
                <li key={k} className={isGroup ? 'is-group' : ''}>
                  <Checkbox
                    indeterminate={someChilrenChecked}
                    name={
                      Array.isArray(option.value)
                        ? option.value.join(',')
                        : option.value
                    }
                    checked={isGroup ? allChilrenChecked : isChecked}
                    onChange={() => handleCheckboxChange(id, option)}
                  >
                    {option.label}
                  </Checkbox>
                </li>
                {option.children?.map?.((childOption, k) => (
                  <li key={k}>
                    <Checkbox
                      name={childOption.value}
                      checked={values.includes(childOption.value)}
                      onChange={() => handleCheckboxChange(id, childOption)}
                    >
                      {childOption.label}
                    </Checkbox>
                  </li>
                ))}
              </Fragment>
            );
          })}
        </ul>
      )}

      {type == FilterTypes.DATE && (
        <div className="mt-6">
          <SubFieldDate
            id={id}
            subFieldId={id}
            handleDateRangeChange={handleDateRangeChange}
            handleRadioChange={handleRadioChange}
            subFieldOnChange={subFieldOnChange}
          />
        </div>
      )}

      <div className="mt-10 align-right">
        {canSelectAll && (
          <>
            {options?.length === values.length ? (
              <button
                className="button simple filter-dropdown-clear-button mr-5"
                title="Deselect all"
                onClick={() => {
                  setFilters({
                    ...filters,
                    [id]: {
                      type: FilterTypes.CHECKBOXES,
                      items: [],
                    },
                  });
                }}
              >
                <FormattedMessage
                  id="filters-deselect-all"
                  description="Deselect all button in filter panels"
                  defaultMessage="Deselect all"
                />
              </button>
            ) : (
              <button
                className="button simple filter-dropdown-clear-button mr-5"
                title="Select all"
                onClick={() => {
                  setFilters({
                    ...filters,
                    [id]: {
                      type: FilterTypes.CHECKBOXES,
                      items: options?.map?.((option) => option.value) ?? [],
                    },
                  });
                }}
              >
                <FormattedMessage
                  id="filters-select-all"
                  description="Select all button in filter panels"
                  defaultMessage="Select all"
                />
              </button>
            )}
          </>
        )}
        <button
          className="button simple filter-dropdown-clear-button"
          title="Clear"
          onClick={() => {
            setVisible(false);
            clearDropdownFilter(id);
          }}
        >
          <FormattedMessage
            id="filters-clear"
            description="Clear button in filter panels"
            defaultMessage="Clear"
          />
        </button>
      </div>
    </>
  );

  const invalid = type === FilterTypes.DATE && values.length !== 2;

  const getActiveLabel = (type, values) => {
    let activeLabel;
    if (type === FilterTypes.DATE && values.length === 2) {
      activeLabel = `${dayjs(values?.[0]).format('D MMM YYYY')} - ${dayjs(
        values?.[1]
      ).format('D MMM YYYY')}`;
    } else if (
      values.length === 1 &&
      Array.isArray(options) &&
      !options.some((opt) => Array.isArray(opt.value))
    ) {
      // Only use findByValue for non-array-value options
      const activeOption = findByValue(options, values[0]);

      if (activeOption?.hasSubField) {
        return getActiveLabel(subFieldType, subValues);
      } else {
        activeLabel = activeOption?.label ?? label;
      }
    } else {
      // Calculate selected count based on options
      let selectedCount = 0;

      options?.forEach((option) => {
        if (option.children && option.children.length > 0) {
          // For grouped options, count children that are selected
          const selectedChildren = option.children.filter((child) =>
            values.includes(child.value)
          );
          selectedCount += selectedChildren.length;
        } else if (Array.isArray(option.value)) {
          // For array values (locations), check if ALL IDs are selected
          if (option.value.every((v) => values.includes(v))) {
            selectedCount += 1;
          }
        } else {
          // For single values, check if it's in the selected values
          if (values.includes(option.value)) {
            selectedCount += 1;
          }
        }
      });

      // If only one option selected, show the option name
      if (selectedCount === 1 && Array.isArray(options)) {
        const singleOption = options.find((option) => {
          if (option.children && option.children.length > 0) {
            // Skip parent options with children
            return false;
          }
          if (Array.isArray(option.value)) {
            return option.value.every((v) => values.includes(v));
          }
          return values.includes(option.value);
        });

        if (singleOption) {
          activeLabel = singleOption.label;
          return activeLabel;
        }
      }

      activeLabel = (
        <>
          {label}{' '}
          {values.length > 0 && showSelectedCount && <>({selectedCount})</>}
        </>
      );
    }
    return activeLabel;
  };

  return (
    <Popover
      content={panel}
      trigger="click"
      placement="bottomLeft"
      visible={visible}
      onVisibleChange={setVisible}
    >
      <button
        className={`filter-dropdown-chip ${
          !invalid && values.length > 0 ? 'active' : ''
        }`}
        onClick={() => setVisible(!visible)}
      >
        {getActiveLabel(type, values)} <Icon type="caret-down" />
      </button>
    </Popover>
  );
};

const SubFieldDate = ({
  id,
  subFieldId,
  option,
  handleDateRangeChange,
  handleRadioChange,
  subFieldOnChange,
}) => {
  const { formatMessage } = useIntl();
  const { filters, setFilters } = useFilters();

  const [dateRange, setDateRange] = useState(
    filters[id]?.items[0] === 'custom'
      ? filters[subFieldId]?.items
      : [null, null]
  );
  const [startDate, endDate] = dateRange;

  useEffect(() => {
    if (filters[id]?.items[0] === 'custom') {
      updateDateRange(dateRange);
    }
  }, [filters[id]?.items[0]]);

  useEffect(() => {
    if (filters[subFieldId]?.items.length === 0) {
      setDateRange([null, null]);
    }
  }, [filters[subFieldId]?.items.length]);

  const updateDateRange = (update) => {
    setDateRange(update);
    if (update[0] && update[1]) {
      const dateRangeState = handleDateRangeChange(subFieldId, update, true);

      const radioChangeState = option
        ? handleRadioChange(id, option, true)
        : {};
      const newFilters = { ...dateRangeState, ...radioChangeState };
      setFilters(newFilters);
      subFieldOnChange && subFieldOnChange(newFilters);
    }
  };

  return (
    <div className="sub-field">
      <DatePicker
        onChange={updateDateRange}
        initialValue={[startDate, endDate]}
      />
    </div>
  );
};

export default FilterDropdown;
